import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import CategoryManager from "../components/CategoryManager";
import PortalTemplatesTab from "../components/portal/PortalTemplatesTab";
import UserAccessControlDialog from "../components/portal/UserAccessControlDialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  Settings,
  Users,
  User,
  Building2,
  Mail,
  Send,
  Eye,
  Trash2,
  Shield,
  Palette,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  Save,
  LayoutDashboard,
  Calendar,
  FileText,
  Briefcase,
  HeartPulse,
  BookOpen,
  UsersRound,
  Package,
  Car,
  Receipt,
  DollarSign,
  BarChart3,
  Bell,
  FolderOpen,
  CreditCard,
  Wallet,
  Landmark,
  Settings2,
  Phone,
  GraduationCap,
  Sparkles,
  Check,
  X,
  Layers,
  Plus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { startImpersonation } from "../lib/impersonation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalSettings = () => {
  const { isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  // Phase 19: portal user access control dialog
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  // Phase 19.1: persona templates loaded for invite-flow selector
  const [inviteTemplates, setInviteTemplates] = useState({ built_in: [], custom: [] });
  // Phase 19.3: invitation permissions preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const openInvitationPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await axios.post(`${API}/portal-templates/invitation-preview`, {
        email: inviteForm.email,
        name: inviteForm.name,
        portal_type: inviteForm.portal_type,
        template_key: inviteForm.template_key || null,
        template_source: inviteForm.template_source || "built_in",
        delegation_widget_enabled: !!inviteForm.delegation_widget_enabled,
      });
      setPreviewData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Settings state
  const [settings, setSettings] = useState({
    company_name: "Business Genie",
    logo_url: "",
    primary_color: "#10b981",
    secondary_color: "#6366f1",
    client_portal_enabled: true,
    client_can_view_quotes: true,
    client_can_accept_quotes: true,
    client_can_view_invoices: true,
    client_can_request_callback: true,
    client_can_message: true,
    client_can_request_quote: true,
    employee_portal_enabled: true,
    employee_can_view_schedule: true,
    employee_can_view_pay_summary: true,
    employee_can_submit_time: true,
    employee_can_view_safety_docs: true,
    employee_can_view_equipment: true,
    employee_can_request_time_off: false,
  });

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  
  // Sidebar customization state
  const [sidebarItems, setSidebarItems] = useState([]);
  const [allowUserCustomization, setAllowUserCustomization] = useState(true);
  const [sidebarSaving, setSidebarSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Default sidebar items configuration
  const DEFAULT_SIDEBAR_ITEMS = [
    { id: "dashboard", label: "Dashboard", href: "/dashboard", icon: "LayoutDashboard", order: 0, enabled: true, color: "", roles: ["office_manager", "quoter", "employee"] },
    { id: "business-profile", label: "Business Profile", href: "/business-profile", icon: "Landmark", order: 1, enabled: true, color: "", roles: ["office_manager"] },
    { id: "portal-settings", label: "Portal Settings", href: "/portal-settings", icon: "Settings2", order: 2, enabled: true, color: "", roles: ["office_manager"] },
    { id: "calendar", label: "Calendar", href: "/calendar", icon: "Calendar", order: 3, enabled: true, color: "", roles: ["office_manager", "quoter", "employee"] },
    { id: "clients", label: "Clients", href: "/clients", icon: "User", order: 4, enabled: true, color: "", roles: ["office_manager", "quoter"] },
    { id: "services", label: "Services & Goods", href: "/services", icon: "Briefcase", order: 5, enabled: true, color: "", roles: ["office_manager"] },
    { id: "quotes", label: "Quotes", href: "/quotes", icon: "FileText", order: 6, enabled: true, color: "", roles: ["office_manager", "quoter", "employee"] },
    { id: "employees", label: "Employees", href: "/employees", icon: "Users", order: 7, enabled: true, color: "", roles: ["office_manager"] },
    { id: "health-safety", label: "Health & Safety", href: "/safety-documents", icon: "HeartPulse", order: 8, enabled: true, color: "", roles: ["office_manager", "quoter", "employee"] },
    { id: "policies", label: "Policies & Procedures", href: "/policies", icon: "BookOpen", order: 9, enabled: true, color: "", roles: ["office_manager", "quoter", "employee"] },
    { id: "union", label: "Union Management", href: "/union", icon: "UsersRound", order: 10, enabled: true, color: "", roles: ["office_manager"] },
    { id: "equipment", label: "Equipment", href: "/equipment", icon: "Package", order: 11, enabled: true, color: "", roles: ["office_manager"] },
    { id: "fleet", label: "Fleet", href: "/fleet", icon: "Car", order: 12, enabled: true, color: "", roles: ["office_manager"] },
    { id: "documents", label: "Document Management", href: "/documents", icon: "FolderOpen", order: 13, enabled: true, color: "", roles: ["office_manager"] },
  ];

  // Icon mapping for sidebar items
  const ICON_MAP = {
    LayoutDashboard, Calendar, FileText, Briefcase, HeartPulse, BookOpen,
    UsersRound, Package, Car, Receipt, DollarSign, BarChart3, Bell,
    FolderOpen, CreditCard, Wallet, Landmark, Settings2, Users, User, Shield
  };

  // Available colors for sidebar items
  const SIDEBAR_COLORS = [
    { value: "default", label: "Default", class: "" },
    { value: "emerald", label: "Emerald", class: "text-emerald-500" },
    { value: "blue", label: "Blue", class: "text-blue-500" },
    { value: "violet", label: "Violet", class: "text-violet-500" },
    { value: "orange", label: "Orange", class: "text-orange-500" },
    { value: "pink", label: "Pink", class: "text-pink-500" },
    { value: "cyan", label: "Cyan", class: "text-cyan-500" },
    { value: "amber", label: "Amber", class: "text-amber-500" },
    { value: "red", label: "Red", class: "text-red-500" },
  ];
  
  // Dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteStep, setInviteStep] = useState(1); // 1: Basic Info, 2: Access Config, 3: Preview
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    portal_type: "client_portal",
    linked_id: "",
    send_email: true,
    access_type: "full_access",
    // Phase 19.1: persona template + delegation widget choice persisted on the invitation
    template_key: "",
    template_source: "built_in",
    delegation_widget_enabled: false,
    custom_permissions: {
      // Client permissions
      can_view_quotes: true,
      can_accept_quotes: true,
      can_view_invoices: true,
      can_pay_invoices: true,
      can_view_schedule: true,
      can_request_callback: true,
      can_message: true,
      can_request_quote: true,
      can_view_documents: true,
      // Employee permissions
      can_view_pay_summary: true,
      can_submit_time: true,
      can_view_safety_docs: true,
      can_view_equipment: true,
      can_request_time_off: true,
      can_view_policies: true,
      can_view_training: true,
    },
  });
  
  // Portal access type presets
  const CLIENT_ACCESS_PRESETS = [
    {
      id: "full_access",
      name: "Full Access",
      description: "Can view and interact with all client features",
      color: "#22c55e",
      permissions: {
        can_view_quotes: true, can_accept_quotes: true, can_view_invoices: true,
        can_pay_invoices: true, can_view_schedule: true, can_request_callback: true,
        can_message: true, can_request_quote: true, can_view_documents: true,
      }
    },
    {
      id: "view_only",
      name: "View Only",
      description: "Can view information but cannot make changes",
      color: "#3b82f6",
      permissions: {
        can_view_quotes: true, can_accept_quotes: false, can_view_invoices: true,
        can_pay_invoices: false, can_view_schedule: true, can_request_callback: false,
        can_message: false, can_request_quote: false, can_view_documents: true,
      }
    },
    {
      id: "payment_only",
      name: "Payment Access",
      description: "Can only view and pay invoices",
      color: "#f59e0b",
      permissions: {
        can_view_quotes: false, can_accept_quotes: false, can_view_invoices: true,
        can_pay_invoices: true, can_view_schedule: false, can_request_callback: false,
        can_message: false, can_request_quote: false, can_view_documents: false,
      }
    },
    {
      id: "custom",
      name: "Custom Access",
      description: "Configure specific permissions",
      color: "#8b5cf6",
      permissions: null,
    },
  ];

  const EMPLOYEE_ACCESS_PRESETS = [
    {
      id: "full_access",
      name: "Full Access",
      description: "Access to all employee portal features",
      color: "#22c55e",
      permissions: {
        can_view_schedule: true, can_view_pay_summary: true, can_submit_time: true,
        can_view_safety_docs: true, can_view_equipment: true, can_request_time_off: true,
        can_view_policies: true, can_view_training: true,
      }
    },
    {
      id: "schedule_only",
      name: "Schedule Only",
      description: "Can only view their schedule",
      color: "#3b82f6",
      permissions: {
        can_view_schedule: true, can_view_pay_summary: false, can_submit_time: false,
        can_view_safety_docs: false, can_view_equipment: false, can_request_time_off: false,
        can_view_policies: false, can_view_training: false,
      }
    },
    {
      id: "field_worker",
      name: "Field Worker",
      description: "Schedule, time entry, safety docs, and equipment",
      color: "#f59e0b",
      permissions: {
        can_view_schedule: true, can_view_pay_summary: false, can_submit_time: true,
        can_view_safety_docs: true, can_view_equipment: true, can_request_time_off: true,
        can_view_policies: true, can_view_training: true,
      }
    },
    {
      id: "custom",
      name: "Custom Access",
      description: "Configure specific permissions",
      color: "#8b5cf6",
      permissions: null,
    },
  ];
  
  // Permission labels with icons
  const CLIENT_PERMISSIONS = [
    { key: "can_view_quotes", label: "View Quotes", icon: FileText, description: "See quotes and proposals" },
    { key: "can_accept_quotes", label: "Accept Quotes", icon: CheckCircle, description: "Approve and accept quotes" },
    { key: "can_view_invoices", label: "View Invoices", icon: Receipt, description: "View invoice history" },
    { key: "can_pay_invoices", label: "Pay Invoices", icon: CreditCard, description: "Make payments online" },
    { key: "can_view_schedule", label: "View Schedule", icon: Calendar, description: "See scheduled appointments" },
    { key: "can_request_callback", label: "Request Callback", icon: Phone, description: "Request a callback" },
    { key: "can_message", label: "Send Messages", icon: MessageSquare, description: "Message your team" },
    { key: "can_request_quote", label: "Request Quote", icon: FileText, description: "Submit quote requests" },
    { key: "can_view_documents", label: "View Documents", icon: FolderOpen, description: "Access shared documents" },
  ];

  const EMPLOYEE_PERMISSIONS = [
    { key: "can_view_schedule", label: "View Schedule", icon: Calendar, description: "See your work schedule" },
    { key: "can_view_pay_summary", label: "View Pay Summary", icon: DollarSign, description: "View pay stubs and earnings" },
    { key: "can_submit_time", label: "Submit Time Entries", icon: Clock, description: "Log work hours" },
    { key: "can_view_safety_docs", label: "Safety Documents", icon: Shield, description: "Access safety resources" },
    { key: "can_view_equipment", label: "View Equipment", icon: Package, description: "See assigned equipment" },
    { key: "can_request_time_off", label: "Request Time Off", icon: Calendar, description: "Submit time off requests" },
    { key: "can_view_policies", label: "View Policies", icon: BookOpen, description: "Access company policies" },
    { key: "can_view_training", label: "Training", icon: GraduationCap, description: "Access training programs" },
  ];
  
  // Lists for dropdowns
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [portalAccessTypes, setPortalAccessTypes] = useState([]);
  const [portalLimits, setPortalLimits] = useState(null);

  // Phase F — Portal Doc Sharing controls (tenant defaults + per-client overrides)
  const [docSharing, setDocSharing] = useState({
    auto_send_to_portal: false,
    doc_strategy: "append",
    custom_categories: [],
  });
  const [docSharingSaving, setDocSharingSaving] = useState(false);
  const [newCustomCategory, setNewCustomCategory] = useState("");
  const [clientOverrides, setClientOverrides] = useState({}); // { client_id: {override, effective} }
  const [overrideSavingId, setOverrideSavingId] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
    }
  }, []);

  const fetchPortalUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/users`);
      setPortalUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch portal users:", error);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, []);

  const fetchTimeOffRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/time-off-requests`);
      setTimeOffRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch time off requests:", error);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  }, []);

  const fetchSidebarConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sidebar-config`);
      if (response.data.sidebar_items) {
        setSidebarItems(response.data.sidebar_items);
      } else {
        setSidebarItems(DEFAULT_SIDEBAR_ITEMS);
      }
      setAllowUserCustomization(response.data.allow_user_customization ?? true);
    } catch (error) {
      console.error("Failed to fetch sidebar config:", error);
      setSidebarItems(DEFAULT_SIDEBAR_ITEMS);
    }
  }, []);

  const fetchPortalLimits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/limits`);
      setPortalLimits(response.data);
    } catch (error) {
      console.error("Failed to fetch portal limits:", error);
    }
  }, []);

  // Phase F — fetch tenant doc-sharing defaults
  const fetchDocSharing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal-settings`);
      setDocSharing({
        auto_send_to_portal: !!res.data?.auto_send_to_portal,
        doc_strategy: res.data?.doc_strategy || "append",
        custom_categories: res.data?.custom_categories || [],
      });
    } catch (error) {
      console.error("Failed to fetch doc-sharing settings:", error);
    }
  }, []);

  // Phase F — save tenant doc-sharing defaults
  const handleSaveDocSharing = async () => {
    setDocSharingSaving(true);
    try {
      await axios.put(`${API}/portal-settings`, {
        auto_send_to_portal: !!docSharing.auto_send_to_portal,
        doc_strategy: docSharing.doc_strategy || "append",
        custom_categories: docSharing.custom_categories || [],
      });
      toast.success("Doc sharing defaults saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save");
    } finally {
      setDocSharingSaving(false);
    }
  };

  // Phase F — load per-client overrides for the clients list
  const fetchClientOverride = useCallback(async (clientId) => {
    try {
      const res = await axios.get(`${API}/portal-settings/client/${clientId}`);
      setClientOverrides((prev) => ({ ...prev, [clientId]: res.data }));
    } catch (error) {
      console.error("Failed to fetch client override:", error);
    }
  }, []);

  // Phase F — save per-client override
  const handleSaveClientOverride = async (clientId, body) => {
    setOverrideSavingId(clientId);
    try {
      await axios.put(`${API}/portal-settings/client/${clientId}`, body);
      await fetchClientOverride(clientId);
      toast.success("Client override saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save override");
    } finally {
      setOverrideSavingId(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSettings(),
        fetchInvitations(),
        fetchPortalUsers(),
        fetchMessages(),
        fetchTimeOffRequests(),
        fetchClients(),
        fetchEmployees(),
        fetchSidebarConfig(),
        fetchPortalLimits(),
        fetchDocSharing(),
      ]);
      // Phase 19.1: load persona templates for invite-flow selector (best-effort)
      try {
        const res = await axios.get(`${API}/portal-templates`);
        setInviteTemplates({ built_in: res.data?.built_in || [], custom: res.data?.custom || [] });
      } catch (_) { /* ignore */ }
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchInvitations, fetchPortalUsers, fetchMessages, fetchTimeOffRequests, fetchClients, fetchEmployees, fetchSidebarConfig, fetchPortalLimits, fetchDocSharing]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/settings`, settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Sidebar customization handlers
  const handleSaveSidebarConfig = async () => {
    setSidebarSaving(true);
    try {
      await axios.put(`${API}/sidebar-config`, {
        sidebar_items: sidebarItems,
        allow_user_customization: allowUserCustomization,
      });
      toast.success("Sidebar configuration saved");
    } catch (error) {
      toast.error("Failed to save sidebar configuration");
    } finally {
      setSidebarSaving(false);
    }
  };

  const handleResetSidebarConfig = async () => {
    if (!window.confirm("Reset sidebar to default configuration?")) return;
    try {
      await axios.delete(`${API}/sidebar-config`);
      setSidebarItems(DEFAULT_SIDEBAR_ITEMS);
      setAllowUserCustomization(true);
      toast.success("Sidebar reset to defaults");
    } catch (error) {
      toast.error("Failed to reset sidebar");
    }
  };

  const moveSidebarItem = (index, direction) => {
    const newItems = [...sidebarItems];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    
    // Swap items
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order values
    newItems.forEach((item, idx) => {
      item.order = idx;
    });
    
    setSidebarItems(newItems);
  };

  const toggleSidebarItem = (itemId) => {
    setSidebarItems(sidebarItems.map(item =>
      item.id === itemId ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const openEditDialog = (item) => {
    setEditingItem({ ...item });
    setEditDialogOpen(true);
  };

  const saveEditItem = () => {
    if (!editingItem) return;
    setSidebarItems(sidebarItems.map(item =>
      item.id === editingItem.id ? editingItem : item
    ));
    setEditDialogOpen(false);
    setEditingItem(null);
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.name || !inviteForm.linked_id) {
      toast.error("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/portal/invitations`, null, {
        params: {
          ...inviteForm,
          permissions: JSON.stringify(inviteForm.custom_permissions),
        }
      });
      toast.success("Invitation sent successfully!");
      setInviteDialogOpen(false);
      resetInviteForm();
      fetchInvitations();
      fetchPortalLimits();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 402 && detail?.code === "PLAN_LIMIT_REACHED") {
        toast.error(detail.message || "Plan limit reached");
        fetchPortalLimits();
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to send invitation");
      }
    } finally {
      setSaving(false);
    }
  };

  const resetInviteForm = () => {
    setInviteForm({
      email: "",
      name: "",
      portal_type: "client_portal",
      linked_id: "",
      send_email: true,
      access_type: "full_access",
      template_key: "",
      template_source: "built_in",
      delegation_widget_enabled: false,
      custom_permissions: {
        can_view_quotes: true, can_accept_quotes: true, can_view_invoices: true,
        can_pay_invoices: true, can_view_schedule: true, can_request_callback: true,
        can_message: true, can_request_quote: true, can_view_documents: true,
        can_view_pay_summary: true, can_submit_time: true, can_view_safety_docs: true,
        can_view_equipment: true, can_request_time_off: true, can_view_policies: true,
        can_view_training: true,
      },
    });
    setInviteStep(1);
  };

  const applyAccessPreset = (presetId) => {
    const presets = inviteForm.portal_type === "client_portal" 
      ? CLIENT_ACCESS_PRESETS 
      : EMPLOYEE_ACCESS_PRESETS;
    const preset = presets.find(p => p.id === presetId);
    
    if (preset && preset.permissions) {
      setInviteForm({
        ...inviteForm,
        access_type: presetId,
        custom_permissions: { ...inviteForm.custom_permissions, ...preset.permissions },
      });
    } else {
      setInviteForm({ ...inviteForm, access_type: presetId });
    }
  };

  const togglePermission = (key) => {
    setInviteForm({
      ...inviteForm,
      access_type: "custom",
      custom_permissions: {
        ...inviteForm.custom_permissions,
        [key]: !inviteForm.custom_permissions[key],
      },
    });
  };

  const getActivePermissions = () => {
    const permissions = inviteForm.portal_type === "client_portal" 
      ? CLIENT_PERMISSIONS 
      : EMPLOYEE_PERMISSIONS;
    return permissions.filter(p => inviteForm.custom_permissions[p.key]);
  };

  const getPresets = () => {
    return inviteForm.portal_type === "client_portal" 
      ? CLIENT_ACCESS_PRESETS 
      : EMPLOYEE_ACCESS_PRESETS;
  };

  const getPermissionsList = () => {
    return inviteForm.portal_type === "client_portal" 
      ? CLIENT_PERMISSIONS 
      : EMPLOYEE_PERMISSIONS;
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/portal/invitations/${invitationId}`);
      toast.success("Invitation revoked");
      fetchInvitations();
    } catch (error) {
      toast.error("Failed to revoke invitation");
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) return;
    try {
      await axios.delete(`${API}/portal/users/${userId}`);
      toast.success("User deactivated");
      fetchPortalUsers();
    } catch (error) {
      toast.error("Failed to deactivate user");
    }
  };

  // Phase 19.15 — "Test as User": ask the backend to validate, store target,
  // navigate to the appropriate portal so the Owner immediately sees what the
  // portal user sees. The orange banner stays until Exit Preview is clicked.
  const handleTestAsUser = async (user) => {
    try {
      const res = await axios.post(`${API}/impersonation/start/${user.id}`);
      const target = res.data?.target;
      if (!target) {
        toast.error("Could not start preview");
        return;
      }
      startImpersonation(target);
      toast.success(`Previewing as ${target.name}`);
      // Land the Owner where the portal user lands. Layout.jsx will redirect
      // based on the (now-impersonated) user's account_type.
      window.location.href = "/";
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to start preview");
    }
  };

  const handleTimeOffDecision = async (requestId, status) => {
    try {
      await axios.put(`${API}/time-off-requests/${requestId}`, null, {
        params: { status }
      });
      toast.success(`Request ${status}`);
      fetchTimeOffRequests();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Portal Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure client and employee portal access
          </p>
        </div>
        <Button
          onClick={() => setInviteDialogOpen(true)}
          disabled={
            portalLimits &&
            !portalLimits.unlimited &&
            !portalLimits.no_tenant &&
            portalLimits.at_limit
          }
          data-testid="portal-invite-btn"
          title={
            portalLimits && portalLimits.at_limit && !portalLimits.unlimited
              ? `Employee portal cap reached (${portalLimits.current_count}/${portalLimits.max_employees}). Upgrade your plan to add more.`
              : "Send a portal invitation"
          }
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Send Invitation
        </Button>
      </div>

      {/* Plan Limit Banner: Employee Portal */}
      {portalLimits && !portalLimits.no_tenant && (
        <Card
          className={
            portalLimits.at_limit
              ? "border-amber-500/40 bg-amber-500/5"
              : ""
          }
          data-testid="employee-portal-limit-card"
        >
          <CardContent className="py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Users
                  className={
                    portalLimits.at_limit
                      ? "h-5 w-5 text-amber-600"
                      : "h-5 w-5 text-primary"
                  }
                />
                <div>
                  <p className="text-sm font-medium">
                    Employee Portal Seats · {portalLimits.plan_name} plan
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {portalLimits.unlimited
                      ? "Unlimited active employees on this plan"
                      : `${portalLimits.current_count} of ${portalLimits.max_employees} active (incl. ${portalLimits.pending_invitations} pending)`}
                  </p>
                </div>
              </div>
              {portalLimits.at_limit && !portalLimits.unlimited && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                  Limit reached
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Client Users</p>
                <p className="text-2xl font-bold">
                  {portalUsers.filter(u => u.account_type === "client_portal").length}
                </p>
              </div>
              <User className="h-8 w-8 text-cyan-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Employee Users</p>
                <p className="text-2xl font-bold">
                  {portalUsers.filter(u => u.account_type === "employee_portal").length}
                </p>
              </div>
              <Users className="h-8 w-8 text-violet-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Invites</p>
                <p className="text-2xl font-bold">
                  {invitations.filter(i => i.status === "pending").length}
                </p>
              </div>
              <Mail className="h-8 w-8 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unread Messages</p>
                <p className="text-2xl font-bold">
                  {messages.filter(m => m.status === "unread").length}
                </p>
              </div>
              <MessageSquare className="h-8 w-8 text-emerald-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="settings" className="flex-1 min-w-[80px]">Settings</TabsTrigger>
          <TabsTrigger value="doc-sharing" className="flex-1 min-w-[80px]" data-testid="doc-sharing-tab">Doc Sharing</TabsTrigger>
          <TabsTrigger value="sidebar" className="flex-1 min-w-[80px]" data-testid="sidebar-tab">Sidebar</TabsTrigger>
          <TabsTrigger value="users" className="flex-1 min-w-[80px]">Users</TabsTrigger>
          <TabsTrigger value="invitations" className="flex-1 min-w-[80px]">Invites</TabsTrigger>
          <TabsTrigger value="messages" className="flex-1 min-w-[80px]">Messages</TabsTrigger>
          <TabsTrigger value="time-off" className="flex-1 min-w-[80px]">Time Off</TabsTrigger>
          <TabsTrigger value="templates" className="flex-1 min-w-[80px]" data-testid="templates-tab">Templates</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Branding
              </CardTitle>
              <CardDescription>Customize how your portals look to clients and employees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logo URL</Label>
                  <Input
                    value={settings.logo_url || ""}
                    onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Portal Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Client Portal
                  </CardTitle>
                  <CardDescription>Control what clients can see and do</CardDescription>
                </div>
                <Switch
                  checked={settings.client_portal_enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, client_portal_enabled: v })}
                />
              </div>
            </CardHeader>
            {settings.client_portal_enabled && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "client_can_view_quotes", label: "View Quotes" },
                    { key: "client_can_accept_quotes", label: "Accept/Decline Quotes" },
                    { key: "client_can_view_invoices", label: "View Invoices" },
                    { key: "client_can_request_callback", label: "Request Callback" },
                    { key: "client_can_message", label: "Send Messages" },
                    { key: "client_can_request_quote", label: "Request New Quotes" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings[item.key]}
                        onCheckedChange={(v) => setSettings({ ...settings, [item.key]: v })}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Employee Portal Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Employee Portal
                  </CardTitle>
                  <CardDescription>Control what employees can see and do</CardDescription>
                </div>
                <Switch
                  checked={settings.employee_portal_enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, employee_portal_enabled: v })}
                />
              </div>
            </CardHeader>
            {settings.employee_portal_enabled && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "employee_can_view_schedule", label: "View Schedule" },
                    { key: "employee_can_view_pay_summary", label: "View Pay Summary" },
                    { key: "employee_can_submit_time", label: "Submit Time Entries" },
                    { key: "employee_can_view_safety_docs", label: "View Safety Documents" },
                    { key: "employee_can_view_equipment", label: "View Assigned Equipment" },
                    { key: "employee_can_request_time_off", label: "Request Time Off" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings[item.key]}
                        onCheckedChange={(v) => setSettings({ ...settings, [item.key]: v })}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </TabsContent>

        {/* Phase F — Doc Sharing Tab (tenant defaults + per-client overrides) */}
        <TabsContent value="doc-sharing" className="space-y-6" data-testid="doc-sharing-content">
          <Card data-testid="doc-sharing-tenant-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" /> Tenant Defaults
              </CardTitle>
              <CardDescription>
                Default rules applied when dispatching quotes, invoices, and other documents to client portals.
                You can override these per client below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Auto-send documents to the client portal</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    When ON, dispatched documents become visible in the client portal by default.
                  </p>
                </div>
                <Switch
                  checked={!!docSharing.auto_send_to_portal}
                  onCheckedChange={(v) => setDocSharing({ ...docSharing, auto_send_to_portal: v })}
                  data-testid="doc-sharing-auto-send"
                />
              </div>

              <div className="p-3 border rounded-lg space-y-2">
                <Label>When a new copy of the same document is sent</Label>
                <p className="text-xs text-muted-foreground">
                  <strong>Append</strong> keeps every version in the client&apos;s portal.
                  <strong> Replace</strong> hides older copies once a newer one arrives.
                </p>
                <div className="flex gap-2 pt-1">
                  {["append", "replace"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setDocSharing({ ...docSharing, doc_strategy: s })}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${docSharing.doc_strategy === s ? "bg-emerald-100 border-emerald-500 text-emerald-700" : "bg-muted/40 hover:bg-muted"}`}
                      data-testid={`doc-sharing-strategy-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-3 border rounded-lg space-y-2">
                <Label>Custom document categories</Label>
                <p className="text-xs text-muted-foreground">
                  Built-in categories (quote, invoice, contract, notice, other) are always available. Add your own below.
                </p>
                <div className="flex gap-2 flex-wrap">
                  {(docSharing.custom_categories || []).map((c) => (
                    <Badge
                      key={c}
                      variant="secondary"
                      className="px-3 py-1 flex items-center gap-1"
                      data-testid={`doc-sharing-custom-cat-${c}`}
                    >
                      {c}
                      <button
                        type="button"
                        onClick={() => setDocSharing({
                          ...docSharing,
                          custom_categories: docSharing.custom_categories.filter((x) => x !== c),
                        })}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <Input
                    value={newCustomCategory}
                    onChange={(e) => setNewCustomCategory(e.target.value)}
                    placeholder="e.g. permit, lien-waiver"
                    className="max-w-[260px]"
                    data-testid="doc-sharing-new-cat-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const v = newCustomCategory.trim().toLowerCase();
                      if (!v) return;
                      if ((docSharing.custom_categories || []).includes(v)) return;
                      setDocSharing({
                        ...docSharing,
                        custom_categories: [...(docSharing.custom_categories || []), v],
                      });
                      setNewCustomCategory("");
                    }}
                    data-testid="doc-sharing-add-cat-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveDocSharing} disabled={docSharingSaving} data-testid="doc-sharing-save-btn">
                  {docSharingSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Defaults
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="doc-sharing-clients-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" /> Per-Client Overrides
              </CardTitle>
              <CardDescription>
                Override the tenant defaults for a specific client. Leave a field as <em>Inherit</em> to use the default above.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(!clients || clients.length === 0) ? (
                <p className="text-center text-muted-foreground py-6">No clients yet — once you add clients you can override per-client here.</p>
              ) : (
                <div className="space-y-2">
                  {clients.map((c) => {
                    const entry = clientOverrides[c.id];
                    const override = entry?.override || { auto_send_to_portal: null, doc_strategy: null };
                    const effective = entry?.effective || { auto_send_to_portal: docSharing.auto_send_to_portal, doc_strategy: docSharing.doc_strategy };
                    const clientName = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.company || c.id;
                    return (
                      <div
                        key={c.id}
                        className="p-3 border rounded-lg"
                        data-testid={`doc-sharing-client-${c.id}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-sm">{clientName}</p>
                            <p className="text-xs text-muted-foreground">
                              Effective: auto-send <strong>{effective.auto_send_to_portal ? "yes" : "no"}</strong>, strategy <strong>{effective.doc_strategy}</strong>
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {!entry && (
                              <Button size="sm" variant="outline" onClick={() => fetchClientOverride(c.id)} data-testid={`doc-sharing-load-${c.id}`}>
                                Load
                              </Button>
                            )}
                            {entry && (
                              <>
                                <Select
                                  value={override.auto_send_to_portal === null ? "inherit" : (override.auto_send_to_portal ? "yes" : "no")}
                                  onValueChange={(val) => {
                                    const v = val === "inherit" ? null : (val === "yes");
                                    handleSaveClientOverride(c.id, { ...override, auto_send_to_portal: v });
                                  }}
                                >
                                  <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`doc-sharing-auto-${c.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="inherit">Auto-send: inherit</SelectItem>
                                    <SelectItem value="yes">Auto-send: yes</SelectItem>
                                    <SelectItem value="no">Auto-send: no</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={override.doc_strategy || "inherit"}
                                  onValueChange={(val) => {
                                    const v = val === "inherit" ? null : val;
                                    handleSaveClientOverride(c.id, { ...override, doc_strategy: v });
                                  }}
                                >
                                  <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`doc-sharing-strat-${c.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="inherit">Strategy: inherit</SelectItem>
                                    <SelectItem value="append">Strategy: append</SelectItem>
                                    <SelectItem value="replace">Strategy: replace</SelectItem>
                                  </SelectContent>
                                </Select>
                                {overrideSavingId === c.id && <Loader2 className="h-4 w-4 animate-spin" />}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sidebar Tab */}
        <TabsContent value="sidebar" className="space-y-6" data-testid="sidebar-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <GripVertical className="h-5 w-5" />
                    Sidebar Navigation
                  </CardTitle>
                  <CardDescription>
                    Customize the sidebar menu for your team. Reorder, rename, or hide items.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleResetSidebarConfig} data-testid="reset-sidebar-btn">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                  <Button onClick={handleSaveSidebarConfig} disabled={sidebarSaving} data-testid="save-sidebar-btn">
                    {sidebarSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sidebarItems
                  .sort((a, b) => a.order - b.order)
                  .map((item, index) => {
                    const IconComp = ICON_MAP[item.icon] || LayoutDashboard;
                    const colorClass = SIDEBAR_COLORS.find(c => c.value === item.color)?.class || "";
                    
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${item.enabled ? "bg-card" : "bg-muted/50 opacity-60"}`}
                        data-testid={`sidebar-item-${item.id}`}
                      >
                        {/* Drag handle / reorder buttons */}
                        <div className="flex flex-col gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => moveSidebarItem(index, "up")}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => moveSidebarItem(index, "down")}
                            disabled={index === sidebarItems.length - 1}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-md bg-muted flex items-center justify-center ${colorClass}`}>
                          <IconComp className="h-4 w-4" />
                        </div>

                        {/* Label */}
                        <div className="flex-1">
                          <p className={`font-medium ${colorClass}`}>{item.label}</p>
                          <p className="text-xs text-muted-foreground">{item.href}</p>
                        </div>

                        {/* Role badges */}
                        <div className="flex gap-1">
                          {item.roles?.includes("office_manager") && (
                            <Badge variant="outline" className="text-xs">Manager</Badge>
                          )}
                          {item.roles?.includes("quoter") && (
                            <Badge variant="outline" className="text-xs">Quoter</Badge>
                          )}
                          {item.roles?.includes("employee") && (
                            <Badge variant="outline" className="text-xs">Employee</Badge>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(item)}
                            data-testid={`edit-sidebar-${item.id}`}
                          >
                            Edit
                          </Button>
                          <Switch
                            checked={item.enabled}
                            onCheckedChange={() => toggleSidebarItem(item.id)}
                            data-testid={`toggle-sidebar-${item.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* User Customization Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                User Permissions
              </CardTitle>
              <CardDescription>
                Control whether team members can customize their own sidebar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Allow User Customization</p>
                  <p className="text-sm text-muted-foreground">
                    When enabled, users can rearrange their own sidebar items
                  </p>
                </div>
                <Switch
                  checked={allowUserCustomization}
                  onCheckedChange={setAllowUserCustomization}
                  data-testid="allow-user-customization"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Portal Users</CardTitle>
              <CardDescription>Active users with portal access</CardDescription>
            </CardHeader>
            <CardContent>
              {portalUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No portal users yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Portal Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portalUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={user.account_type === "client_portal" ? "default" : "secondary"}>
                            {user.account_type === "client_portal" ? "Client" : "Employee"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? "outline" : "destructive"}>
                            {user.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleTestAsUser(user)}
                            data-testid={`test-as-user-${user.id}`}
                            title="Preview the app as this portal user"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" /> Test as User
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mr-1"
                            onClick={() => { setAccessUser(user); setAccessDialogOpen(true); }}
                            data-testid={`manage-access-${user.id}`}
                          >
                            <Shield className="h-3.5 w-3.5 mr-1" /> Manage Access
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeactivateUser(user.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations">
          <Card>
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
              <CardDescription>Pending and past invitations</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No invitations sent yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Portal Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">{inv.name}</TableCell>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>
                          <Badge variant={inv.portal_type === "client_portal" ? "default" : "secondary"}>
                            {inv.portal_type === "client_portal" ? "Client" : "Employee"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            inv.status === "pending" ? "outline" :
                            inv.status === "accepted" ? "default" : "destructive"
                          }>
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          {inv.status === "pending" && (
                            <Button variant="ghost" size="icon" onClick={() => handleRevokeInvitation(inv.id)}>
                              <XCircle className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle>Client Messages</CardTitle>
              <CardDescription>Messages from client portal users</CardDescription>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No messages yet</p>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`p-4 border rounded-lg ${msg.status === "unread" ? "bg-accent/50" : ""}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{msg.subject}</p>
                          <p className="text-sm text-muted-foreground">From: {msg.client_name}</p>
                        </div>
                        <Badge variant={
                          msg.status === "unread" ? "default" :
                          msg.status === "responded" ? "outline" : "secondary"
                        }>
                          {msg.status}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm">{msg.message}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Off Tab */}
        <TabsContent value="time-off">
          <Card>
            <CardHeader>
              <CardTitle>Time Off Requests</CardTitle>
              <CardDescription>Employee time off requests</CardDescription>
            </CardHeader>
            <CardContent>
              {timeOffRequests.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No time off requests</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timeOffRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell>{req.start_date} to {req.end_date}</TableCell>
                        <TableCell>{req.reason}</TableCell>
                        <TableCell>
                          <Badge variant={
                            req.status === "pending" ? "outline" :
                            req.status === "approved" ? "default" : "destructive"
                          }>
                            {req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {req.status === "pending" && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTimeOffDecision(req.id, "approved")}
                              >
                                <CheckCircle className="h-4 w-4 text-emerald-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTimeOffDecision(req.id, "denied")}
                              >
                                <XCircle className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Phase 19: Portal User Templates */}
        <TabsContent value="templates" data-testid="templates-content">
          <PortalTemplatesTab />
        </TabsContent>
      </Tabs>

      {/* Phase 19: Manage portal user access control dialog */}
      <UserAccessControlDialog
        open={accessDialogOpen}
        onOpenChange={setAccessDialogOpen}
        portalUser={accessUser}
        onSaved={() => { /* nothing extra; list doesn't show permissions yet */ }}
      />

      {/* Phase 19.3: Invitation Permissions Preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="invitation-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" /> Invitation Permissions Preview
            </DialogTitle>
            <DialogDescription>
              This is what {previewData?.recipient_name || "the recipient"} will get when they accept the invitation.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !previewData ? (
            <p className="text-sm text-muted-foreground py-4">Loading preview…</p>
          ) : (
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm rounded-xl border bg-muted/30 p-3">
                <div>
                  <p className="text-xs text-muted-foreground">Recipient</p>
                  <p className="font-medium">{previewData.recipient_name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{previewData.recipient_email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Template</p>
                  <p className="font-medium">{previewData.template_name || "(none)"}</p>
                  {previewData.template_description && (
                    <p className="text-xs text-muted-foreground">{previewData.template_description}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Portal Type</p>
                  <Badge variant="outline">
                    {previewData.portal_type === "client_portal" ? "Client Portal" : "Employee Portal"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Permissions Granted</p>
                  <p className="font-semibold text-base">
                    <span className="text-emerald-600">{previewData.granted_count}</span>
                    <span className="text-muted-foreground"> / {previewData.total_count}</span>
                  </p>
                </div>
                {previewData.allows_delegation && (
                  <div className="sm:col-span-2 flex items-center gap-2 text-xs">
                    <Badge variant="secondary">Delegation</Badge>
                    <span>
                      {previewData.delegation_widget_enabled
                        ? "Dashboard widget will be visible to this user."
                        : "Delegation enabled, widget hidden until you flip it on."}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold mb-2">Permissions Matrix</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="permissions-matrix">
                  {previewData.permissions_matrix.map((row) => (
                    <div
                      key={row.key}
                      className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${
                        row.granted
                          ? "border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20"
                          : "border-muted-foreground/20 bg-muted/30"
                      }`}
                      data-testid={`perm-row-${row.key}`}
                    >
                      <span className="truncate">{row.label}</span>
                      {row.granted ? (
                        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="close-preview-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => { setInviteDialogOpen(open); if (!open) resetInviteForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Send Portal Invitation
            </DialogTitle>
            <DialogDescription>
              {inviteStep === 1 && "Step 1: Select who to invite"}
              {inviteStep === 2 && "Step 2: Configure access permissions"}
              {inviteStep === 3 && "Step 3: Preview and send"}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 py-4 border-b">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    inviteStep >= step 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-1 mx-1 rounded ${inviteStep > step ? "bg-primary" : "bg-muted"}`} />
                )}
              </div>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {/* Step 1: Basic Info */}
            {inviteStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Portal User Type</Label>
                    <CategoryManager
                      categoryType="portal_user_type"
                      label="Custom User Types"
                      compact={true}
                      onCategoriesChange={() => {}}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Card 
                    className={`cursor-pointer transition-all ${inviteForm.portal_type === "client_portal" ? "ring-2 ring-primary" : "hover:border-primary/50"}`}
                    onClick={() => setInviteForm({ ...inviteForm, portal_type: "client_portal", linked_id: "", access_type: "full_access" })}
                  >
                    <CardContent className="pt-6 text-center">
                      <User className="h-10 w-10 mx-auto mb-2 text-cyan-500" />
                      <h3 className="font-semibold text-sm">Client Portal</h3>
                      <p className="text-xs text-muted-foreground mt-1">For customers</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className={`cursor-pointer transition-all ${inviteForm.portal_type === "employee_portal" ? "ring-2 ring-primary" : "hover:border-primary/50"}`}
                    onClick={() => setInviteForm({ ...inviteForm, portal_type: "employee_portal", linked_id: "", access_type: "full_access" })}
                  >
                    <CardContent className="pt-6 text-center">
                      <Users className="h-10 w-10 mx-auto mb-2 text-orange-500" />
                      <h3 className="font-semibold text-sm">Employee Portal</h3>
                      <p className="text-xs text-muted-foreground mt-1">For team members</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>
                      {inviteForm.portal_type === "client_portal" 
                        ? "Select Client *" 
                        : "Select Employee *"}
                    </Label>
                    <Select
                      value={inviteForm.linked_id}
                      onValueChange={(v) => {
                        const list = inviteForm.portal_type === "client_portal" ? clients : employees;
                        const item = list.find(i => i.id === v);
                        setInviteForm({
                          ...inviteForm,
                          linked_id: v,
                          name: item?.name || `${item?.first_name || ""} ${item?.last_name || ""}`.trim(),
                          email: item?.emails?.[0]?.email || item?.email || ""
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a person..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(inviteForm.portal_type === "client_portal" ? clients : employees).map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name || `${item.first_name} ${item.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Address *</Label>
                      <Input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Access Configuration */}
            {inviteStep === 2 && (
              <div className="space-y-6">
                {/* Phase 19.1: Persona Template selector — applied on invitation acceptance */}
                <div className="rounded-xl border-2 border-primary/20 p-4 bg-primary/5">
                  <Label className="text-base font-semibold mb-2 block">
                    Persona Template <span className="text-xs font-normal text-muted-foreground">(applied when user accepts invite)</span>
                  </Label>
                  <Select
                    value={inviteForm.template_key ? `${inviteForm.template_source}::${inviteForm.template_key}` : "none"}
                    onValueChange={(v) => {
                      if (v === "none") {
                        setInviteForm({ ...inviteForm, template_key: "", template_source: "built_in", delegation_widget_enabled: false });
                        return;
                      }
                      const [src, key] = v.split("::");
                      // Detect allows_delegation
                      const personaList = inviteTemplates?.built_in || [];
                      const customList = inviteTemplates?.custom || [];
                      let allowsDeleg = false;
                      if (src === "built_in") allowsDeleg = !!personaList.find((p) => p.key === key)?.allows_delegation;
                      else allowsDeleg = !!customList.find((t) => t.id === key)?.allows_delegation;
                      setInviteForm({
                        ...inviteForm,
                        template_key: key,
                        template_source: src,
                        delegation_widget_enabled: allowsDeleg ? inviteForm.delegation_widget_enabled : false,
                      });
                    }}
                  >
                    <SelectTrigger data-testid="invite-template-select">
                      <SelectValue placeholder="Choose a template…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No template (legacy custom permissions below) —</SelectItem>
                      {(inviteTemplates?.built_in || [])
                        .filter((p) => p.category === inviteForm.portal_type)
                        .map((p) => (
                          <SelectItem key={p.key} value={`built_in::${p.key}`}>
                            {p.name}{p.allows_delegation ? " · delegation" : ""}
                          </SelectItem>
                        ))}
                      {(inviteTemplates?.custom || [])
                        .filter((t) => t.category === inviteForm.portal_type)
                        .map((t) => (
                          <SelectItem key={t.id} value={`custom::${t.id}`}>
                            ✦ {t.name} (Custom)
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const personaList = inviteTemplates?.built_in || [];
                    const customList = inviteTemplates?.custom || [];
                    let allowsDeleg = false;
                    if (inviteForm.template_source === "built_in") allowsDeleg = !!personaList.find((p) => p.key === inviteForm.template_key)?.allows_delegation;
                    else allowsDeleg = !!customList.find((t) => t.id === inviteForm.template_key)?.allows_delegation;
                    if (!allowsDeleg) return null;
                    return (
                      <div className="flex items-center justify-between gap-2 mt-3 rounded-md border bg-card px-3 py-2">
                        <div>
                          <Label className="text-xs font-semibold">Show Delegation Widget on Dashboard</Label>
                          <p className="text-[11px] text-muted-foreground">
                            Adds a "Portal Users I Manage" card so this Assistant Manager can act on their delegated users. You can toggle this anytime.
                          </p>
                        </div>
                        <Switch
                          checked={!!inviteForm.delegation_widget_enabled}
                          onCheckedChange={(v) => setInviteForm({ ...inviteForm, delegation_widget_enabled: v })}
                          data-testid="invite-delegation-widget-switch"
                        />
                      </div>
                    );
                  })()}
                </div>

                {/* Access Type Presets */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Access Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {getPresets().map((preset) => (
                      <Card
                        key={preset.id}
                        className={`cursor-pointer transition-all ${
                          inviteForm.access_type === preset.id 
                            ? "ring-2 ring-primary bg-primary/5" 
                            : "hover:border-primary/50"
                        }`}
                        onClick={() => applyAccessPreset(preset.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: preset.color + "20", color: preset.color }}
                            >
                              {preset.id === "full_access" && <CheckCircle className="h-5 w-5" />}
                              {preset.id === "view_only" && <Eye className="h-5 w-5" />}
                              {preset.id === "payment_only" && <CreditCard className="h-5 w-5" />}
                              {preset.id === "schedule_only" && <Calendar className="h-5 w-5" />}
                              {preset.id === "field_worker" && <Package className="h-5 w-5" />}
                              {preset.id === "custom" && <Settings2 className="h-5 w-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium">{preset.name}</h4>
                              <p className="text-xs text-muted-foreground mt-0.5">{preset.description}</p>
                            </div>
                            {inviteForm.access_type === preset.id && (
                              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Custom Permissions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold">Permissions</Label>
                    <Badge variant="outline">
                      {getActivePermissions().length} of {getPermissionsList().length} enabled
                    </Badge>
                  </div>
                  <div className="border rounded-lg divide-y">
                    {getPermissionsList().map((perm) => {
                      const Icon = perm.icon;
                      const isEnabled = inviteForm.custom_permissions[perm.key];
                      return (
                        <div
                          key={perm.key}
                          className={`flex items-center gap-4 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                            !isEnabled && "opacity-50"
                          }`}
                          onClick={() => togglePermission(perm.key)}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{perm.label}</p>
                            <p className="text-xs text-muted-foreground">{perm.description}</p>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => togglePermission(perm.key)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Preview */}
            {inviteStep === 3 && (
              <div className="space-y-6">
                {/* Summary Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Invitation Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Name</p>
                        <p className="font-medium">{inviteForm.name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-medium">{inviteForm.email}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Portal Type</p>
                        <Badge variant={inviteForm.portal_type === "client_portal" ? "default" : "secondary"}>
                          {inviteForm.portal_type === "client_portal" ? "Client Portal" : "Employee Portal"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Access Level</p>
                        <Badge variant="outline" className="capitalize">
                          {inviteForm.access_type.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    </div>
                    {inviteForm.template_key && (
                      <div className="pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openInvitationPreview}
                          data-testid="preview-permissions-btn"
                          className="w-full"
                        >
                          <Eye className="h-4 w-4 mr-1.5" /> Preview Permissions Matrix
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Portal Preview */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Portal Preview
                    </CardTitle>
                    <CardDescription>
                      This is what {inviteForm.name?.split(" ")[0] || "the user"} will see when they log in
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden bg-background">
                      {/* Mock Portal Header */}
                      <div className="bg-[#0a1628] text-white p-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <Briefcase className="h-4 w-4" />
                        </div>
                        <span className="font-semibold">Business Genie</span>
                        <div className="ml-auto flex items-center gap-2">
                          <span className="text-sm opacity-70">{inviteForm.name}</span>
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Mock Portal Sidebar & Content */}
                      <div className="flex min-h-[250px]">
                        {/* Sidebar */}
                        <div className="w-48 bg-muted/30 border-r p-3 space-y-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Menu</p>
                          {getActivePermissions().slice(0, 6).map((perm) => {
                            const Icon = perm.icon;
                            return (
                              <div key={perm.key} className="flex items-center gap-2 p-2 rounded text-sm hover:bg-muted cursor-pointer">
                                <Icon className="h-4 w-4 text-muted-foreground" />
                                <span>{perm.label}</span>
                              </div>
                            );
                          })}
                          {getActivePermissions().length > 6 && (
                            <p className="text-xs text-muted-foreground pl-2">+{getActivePermissions().length - 6} more...</p>
                          )}
                        </div>
                        
                        {/* Content Area */}
                        <div className="flex-1 p-4">
                          <h3 className="font-semibold mb-2">
                            Welcome, {inviteForm.name?.split(" ")[0] || "User"}!
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            {inviteForm.portal_type === "client_portal"
                              ? "View your quotes, invoices, and manage your account."
                              : "Check your schedule, submit time entries, and more."}
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            {getActivePermissions().slice(0, 4).map((perm) => {
                              const Icon = perm.icon;
                              return (
                                <div key={perm.key} className="border rounded-lg p-3 bg-card">
                                  <Icon className="h-5 w-5 text-primary mb-2" />
                                  <p className="text-sm font-medium">{perm.label}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Send Email Option */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div>
                    <p className="font-medium">Send Invitation Email</p>
                    <p className="text-sm text-muted-foreground">
                      {inviteForm.send_email 
                        ? `An email with login instructions will be sent to ${inviteForm.email}`
                        : "No email will be sent. Share login details manually."}
                    </p>
                  </div>
                  <Switch
                    checked={inviteForm.send_email}
                    onCheckedChange={(v) => setInviteForm({ ...inviteForm, send_email: v })}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex justify-between w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => inviteStep > 1 ? setInviteStep(inviteStep - 1) : setInviteDialogOpen(false)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {inviteStep > 1 ? "Back" : "Cancel"}
              </Button>
              
              {inviteStep < 3 ? (
                <Button
                  type="button"
                  onClick={() => setInviteStep(inviteStep + 1)}
                  disabled={inviteStep === 1 && (!inviteForm.linked_id || !inviteForm.email || !inviteForm.name)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSendInvitation} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sidebar Item Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sidebar Item</DialogTitle>
            <DialogDescription>
              Customize this navigation item
            </DialogDescription>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={editingItem.label}
                  onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                  data-testid="edit-item-label"
                />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={editingItem.color || "default"}
                  onValueChange={(v) => setEditingItem({ ...editingItem, color: v === "default" ? "" : v })}
                >
                  <SelectTrigger data-testid="edit-item-color">
                    <SelectValue placeholder="Default color" />
                  </SelectTrigger>
                  <SelectContent>
                    {SIDEBAR_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${color.class || "bg-muted"}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Visible to Roles</Label>
                <div className="flex gap-2 flex-wrap">
                  {["office_manager", "quoter", "employee"].map((role) => (
                    <Button
                      key={role}
                      type="button"
                      variant={editingItem.roles?.includes(role) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        const roles = editingItem.roles || [];
                        const newRoles = roles.includes(role)
                          ? roles.filter(r => r !== role)
                          : [...roles, role];
                        setEditingItem({ ...editingItem, roles: newRoles });
                      }}
                      data-testid={`role-toggle-${role}`}
                    >
                      {role === "office_manager" ? "Manager" : role.charAt(0).toUpperCase() + role.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              {/* Preview */}
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <div className="flex items-center gap-3">
                  {(() => {
                    const IconComp = ICON_MAP[editingItem.icon] || LayoutDashboard;
                    const colorClass = SIDEBAR_COLORS.find(c => c.value === editingItem.color)?.class || "";
                    return (
                      <>
                        <div className={`w-8 h-8 rounded-md bg-background flex items-center justify-center ${colorClass}`}>
                          <IconComp className="h-4 w-4" />
                        </div>
                        <span className={`font-medium ${colorClass}`}>{editingItem.label}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEditItem} data-testid="save-edit-item-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalSettings;
